import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ItemDoMenu {
  readonly rotulo: string;
  readonly aoClicar: () => void;
  /** Ação destrutiva — aparece em tom de vencido. */
  readonly perigo?: boolean;
  readonly desabilitado?: boolean;
}

/**
 * Menu de três traços (hambúrguer) para ações da linha.
 *
 * Renderiza o painel em portal com posição fixa: dentro de `.rolagem-lateral` o
 * `overflow` cortaria o menu absoluto no fim da tabela.
 */
export function MenuAcoes({
  itens,
  rotulo = 'Ações',
}: {
  itens: readonly ItemDoMenu[];
  rotulo?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const idDoMenu = useId();

  useLayoutEffect(() => {
    if (!aberto || !botao.current) {
      setPos(null);
      return;
    }
    const r = botao.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 4,
      right: window.innerWidth - r.right,
    });
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    function fecharFora(e: MouseEvent) {
      const t = e.target as Node;
      if (botao.current?.contains(t) || painel.current?.contains(t)) return;
      setAberto(false);
    }
    function fecharEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setAberto(false);
    }
    function fecharScroll() {
      setAberto(false);
    }
    document.addEventListener('mousedown', fecharFora);
    document.addEventListener('keydown', fecharEsc);
    window.addEventListener('scroll', fecharScroll, true);
    return () => {
      document.removeEventListener('mousedown', fecharFora);
      document.removeEventListener('keydown', fecharEsc);
      window.removeEventListener('scroll', fecharScroll, true);
    };
  }, [aberto]);

  if (itens.length === 0) return null;

  return (
    <>
      <button
        ref={botao}
        type="button"
        aria-label={rotulo}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={idDoMenu}
        onClick={(e) => {
          e.stopPropagation();
          setAberto((v) => !v);
        }}
        className={
          'inline-flex size-8 items-center justify-center rounded-md text-[color:var(--texto-suave)] ' +
          'transition-colors hover:bg-[color:var(--superficie-afundada)] hover:text-[color:var(--texto)]'
        }
      >
        <IconeMenu />
      </button>

      {aberto &&
        pos &&
        createPortal(
          <div
            ref={painel}
            id={idDoMenu}
            role="menu"
            className={
              'fixed z-[80] min-w-[10rem] overflow-hidden rounded-md border ' +
              'bg-[color:var(--superficie-elevada)] py-1 shadow-lg'
            }
            style={{
              top: pos.top,
              right: pos.right,
              boxShadow: 'var(--sombra-cartao)',
            }}
          >
            {itens.map((item) => (
              <button
                key={item.rotulo}
                type="button"
                role="menuitem"
                disabled={item.desabilitado}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.desabilitado) return;
                  setAberto(false);
                  item.aoClicar();
                }}
                className={
                  'block w-full px-3 py-2 text-left text-sm transition-colors ' +
                  (item.desabilitado
                    ? 'cursor-not-allowed text-[color:var(--texto-tenue)]'
                    : item.perigo
                      ? 'text-[color:var(--color-vencido)] hover:bg-[color:var(--superficie-afundada)]'
                      : 'text-[color:var(--texto)] hover:bg-[color:var(--superficie-afundada)]')
                }
              >
                {item.rotulo}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

function IconeMenu(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <rect x="2" y="3.5" width="12" height="1.5" rx="0.75" />
      <rect x="2" y="7.25" width="12" height="1.5" rx="0.75" />
      <rect x="2" y="11" width="12" height="1.5" rx="0.75" />
    </svg>
  );
}
